// ============================================
// Age Verification (18+) Approval Hooks — TanStack Query
// ============================================
// Duyệt hồ sơ xác minh 18+ của khách. Quyền: STORE_ADMIN | AI_ADMIN.
//
// API (chốt với BE 06/08/2026):
// - GET  /customers/{customer_id}/age-verification/  — chi tiết 1 hồ sơ
// - POST /customers/{customer_id}/age-verification/  — duyệt/từ chối
// - GET  /customers/age-verifications/               — danh sách chờ duyệt
//   (BE đang bổ sung endpoint này — contract xem customers.service.ts)

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import type { ReviewAgeVerificationRequest, UUID } from '@conai/shared-types';
import { useCustomersService } from '../providers/ApiProvider';
import { queryKeys } from '../config/query-client';

export interface AgeVerificationListParams {
  status?: string;
  search?: string;
  page?: number;
  page_size?: number;
}

/** Danh sách hồ sơ (mặc định PENDING — hàng đợi chờ duyệt). */
export function useAgeVerifications(params: AgeVerificationListParams = {}) {
  const customersService = useCustomersService();
  const merged = { status: 'PENDING', ...params };

  return useQuery({
    queryKey: queryKeys.customers.ageVerificationList(merged as Record<string, unknown>),
    queryFn: () => customersService.listAgeVerifications(merged),
    // Hàng đợi duyệt đổi liên tục (khách nộp mới) — luôn hỏi lại khi mở màn
    staleTime: 0,
    refetchOnMount: 'always',
  });
}

/** Chi tiết hồ sơ của một khách (ảnh giấy tờ, phương thức, thời điểm nộp). */
export function useAgeVerificationDetail(customerId?: UUID | null) {
  const customersService = useCustomersService();

  return useQuery({
    queryKey: queryKeys.customers.ageVerificationDetail(customerId ?? 'none'),
    queryFn: () => customersService.getCustomerAgeVerification(customerId as UUID),
    enabled: !!customerId,
  });
}

/** Duyệt hoặc từ chối hồ sơ. */
export function useReviewAgeVerification() {
  const customersService = useCustomersService();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      customerId,
      data,
    }: {
      customerId: UUID;
      data: ReviewAgeVerificationRequest;
    }) => customersService.reviewAgeVerification(customerId, data),
    onSuccess: (_result, { customerId, data }) => {
      // ⚠️ TUYỆT ĐỐI KHÔNG `setQueryData` bằng response của POST này.
      //
      // Từ 17/08/2026 backend XOÁ ảnh giấy tờ ngay khi có quyết định duyệt/từ
      // chối (yêu cầu tối thiểu hoá dữ liệu RODO), nên response trả về
      // `document_urls: []`, `document_url: null`, `date_of_birth: null`. Ghi
      // nó vào cache chi tiết là tự tay làm trống ảnh đang hiển thị trên màn
      // hình ngay giữa lúc admin còn đang nhìn — trông y như mất dữ liệu.
      //
      // Xoá hẳn khỏi cache thay vì ghi đè: lần sau mở hồ sơ này sẽ hỏi lại
      // backend, không phục vụ một bản ghi rỗng từ cache.
      queryClient.removeQueries({
        queryKey: queryKeys.customers.ageVerificationDetail(customerId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.customers.ageVerifications(),
      });
      // Hồ sơ khách trong màn Customers cũng đổi trạng thái
      queryClient.invalidateQueries({ queryKey: queryKeys.customers.lists() });
      toast.success(
        data.status === 'VERIFIED'
          ? 'Đã duyệt xác minh 18+'
          : 'Đã từ chối hồ sơ xác minh'
      );
    },
  });
}
